import React from 'react';
import Select, { components } from 'react-select';
import { FormControl, InputLabel } from '@mui/material';

interface SearchableSelectProps {
  label: string;
  options: { value: string | number; label: string }[];
  value: string | number;
  onChange: (value: { value: string | number; label: string } | null) => void;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  sx?: any;
  disabled?: boolean;
  endAdornment?: React.ReactNode;
  required?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  options,
  value,
  onChange,
  fullWidth = false,
  size = 'small',
  sx = {},
  disabled = false,
  endAdornment,
  required = false,
}) => {
  const selectedOption = options.find(option => option.value === value);
  const selectOptions = options;

  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: size === 'small' ? '32px' : '42px',
      borderRadius: '4px',
      borderColor: state.isFocused ? '#1976d2' : '#c4c4c4',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(25, 118, 210, 0.2)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#1976d2' : '#666',
      },
      backgroundColor: disabled ? '#f5f5f5' : 'white',
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: size === 'small' ? '0 8px' : '2px 8px',
    }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: 'inherit',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: size === 'small' ? '32px' : '42px',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: '#666',
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 999,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? 'rgba(0, 0, 0, 0.23)'
        : state.isFocused 
          ? '#f5f5f5' 
          : 'transparent',
      color: state.isSelected ? '#000' : base.color,
      '&:active': {
        backgroundColor: 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover': {
        backgroundColor: state.isSelected 
          ? 'rgba(0, 0, 0, 0.23)' 
          : '#f5f5f5',
      }
    }),
  };

  const LoadingIndicator = (props: any) => {
    return (
      <components.LoadingIndicator {...props}>
        {endAdornment}
      </components.LoadingIndicator>
    );
  };

  return (
    <FormControl fullWidth={fullWidth} sx={sx}>
      <Select
        value={selectedOption ? { value: selectedOption.value, label: selectedOption.label } : null}
        onChange={(newValue: any) => {
          onChange(newValue ? {
            value: newValue.value,
            label: newValue.label
          } : null);
        }}
        options={selectOptions}
        isDisabled={disabled}
        styles={customStyles}
        isClearable
        isSearchable
        required={required}
        placeholder={label}
        components={{
          LoadingIndicator,
          IndicatorSeparator: () => null
        }}
      />
    </FormControl>
  );
};

export default SearchableSelect; 